import type { Schema } from '@lemonade-hq/chat-engine';
import type {
  ConditionalItems,
  OptionallyConditionalItem,
  WizardConditions,
  WizardOnChangeDataDependencies,
} from '@lemonade-hq/chat-wizard-common-definitions';
import type { GenericSchemaDataValue, RecordPath } from '@lemonade-hq/maschema-schema';
import type { IconName, SwitchProps } from '@lemonade-hq/uniclient-theme';
import type { UniclientAction, UniclientSaveDynamicListValuesAction } from '../action';
import type {
  MappedInterpolationOrInterpolation,
  UniclientConditionalMappedInterpolationOrInterpolation,
  UniclientOptionalConditionalMappedInterpolationOrInterpolation,
} from '../mappedInterpolation';
import type { UniclientWizardComponent } from '../step';
import type { GridResponsiveColumns } from '../utils';
import type { FileTypePreset } from '../utils/fileUpload';
import type { UniclientBaseComponentProps, UniclientWizardComponentBase } from './types';

export const FILE_UPLOAD_FILE_BINARY = '__FILE_UPLOAD_FILE_BINARY__';
export const FILE_UPLOAD_FILE_TYPE = '__FILE_UPLOAD_FILE_TYPE__';
export const FILE_UPLOAD_FILE_NAME = '__FILE_UPLOAD_FILE_NAME__';

export interface FileUploadRequestMap {
  readonly url: string;
  readonly formData: Record<string, string>;
}

export type FileUploadResponseMap = Record<string, string> & {
  readonly url: string;
};

interface BaseTriggerProps {
  readonly visibleOnHover?: boolean;
}

export interface IconButtonProps extends BaseTriggerProps {
  readonly type: 'iconButton';
  readonly icon: IconName;
  readonly action: UniclientAction;
}

export interface SwitchTriggerProps extends BaseTriggerProps {
  readonly type: 'switch';
  readonly checkedAttribute: string;
  readonly variant?: SwitchProps['variant'];
}

export interface TextTriggerProps extends BaseTriggerProps {
  readonly type: 'text';
  readonly text: string;
}

export type DynamicListActionTrigger = IconButtonProps | SwitchTriggerProps | TextTriggerProps;

function iconButton(props: Omit<IconButtonProps, 'type'>): IconButtonProps {
  return { ...props, type: 'iconButton' };
}

function switchTrigger(props: Omit<SwitchTriggerProps, 'type'>): SwitchTriggerProps {
  return { ...props, type: 'switch' };
}

function textTrigger(props: Omit<TextTriggerProps, 'type'>): TextTriggerProps {
  return { ...props, type: 'text' };
}

export const dynamicListItemActions = {
  iconButton,
  switchTrigger,
  textTrigger,
};

export const DYNAMIC_LIST_CURRENT_ITERABLE_ATTRIBUTE = '__currentIterableAttribute__';
export const DYNAMIC_LIST_ITERABLE_ATTRIBUTE = '__iterableAttribute__';

export enum DynamicListItemType {
  Card = 'card',
  File = 'file',
}

export interface BaseDynamicListItem<TType extends DynamicListItemType> {
  readonly type: TType;
}
export interface DynamicListCardItemProps<TSchema extends Schema = Schema, TAnswersAttributes extends string = string>
  extends BaseDynamicListItem<DynamicListItemType.Card> {
  readonly id: string;
  readonly image?: MappedInterpolationOrInterpolation<TSchema, TAnswersAttributes>;
  readonly title: UniclientConditionalMappedInterpolationOrInterpolation<TSchema, TAnswersAttributes>;
  readonly subtitle?: UniclientOptionalConditionalMappedInterpolationOrInterpolation<TSchema, TAnswersAttributes>;
  readonly badge?: UniclientOptionalConditionalMappedInterpolationOrInterpolation<TSchema, TAnswersAttributes>;
  readonly actionTriggers?: OptionallyConditionalItem<DynamicListActionTrigger>[];
  readonly disabled?: WizardConditions[] | boolean;
}

export type FileStatus = 'error' | 'pending' | 'success';

export interface DynamicListFileItemProps<TSchema extends Schema = Schema>
  extends BaseDynamicListAddItemProps<DynamicListItemType.File, TSchema> {
  readonly id: string;
  readonly fileName: string;
  readonly url?: string;
  readonly status?: FileStatus;
  readonly fileType?: string;
}

export type DynamicListItemProps<TSchema extends Schema = Schema, TAnswersAttributes extends string = string> =
  | DynamicListCardItemProps<TSchema, TAnswersAttributes>
  | DynamicListFileItemProps<TSchema>;

// GENERAL
interface BaseDynamicListAddItemProps<
  TItemType extends DynamicListItemType = DynamicListItemType,
  TSchema extends Schema = Schema,
> {
  readonly type: TItemType;
  readonly data?: {
    readonly initialValues?: Partial<Record<RecordPath<NoInfer<TSchema>>, GenericSchemaDataValue>>;
    readonly schema: TSchema;
    readonly changeDependencies?: WizardOnChangeDataDependencies<TSchema>;
  };
}

export interface DynamicListCardAddItemProps<
  TComponentAttributes extends string = string,
  TUIAttributes extends string = string,
  TSchema extends Schema = Schema,
  TInternalAttributes extends string = string,
> extends BaseDynamicListAddItemProps<DynamicListItemType.Card, TSchema> {
  readonly title: ConditionalItems<{ readonly text: string }> | string;
  readonly submitTitle?:
    | OptionallyConditionalItem<{
        readonly text: string;
      }>
    | string;
  readonly component?: UniclientWizardComponent<TComponentAttributes | TInternalAttributes, TUIAttributes>;
  readonly action: UniclientAction<TComponentAttributes | TInternalAttributes, TUIAttributes>;
  readonly closeAction: UniclientAction<TComponentAttributes | TInternalAttributes, TUIAttributes>;
  readonly additionalAction?:
    | ConditionalItems<UniclientAction<TComponentAttributes | TInternalAttributes, TUIAttributes>>
    | UniclientAction<TComponentAttributes | TInternalAttributes, TUIAttributes>;
}

export type DynamicListAddItemProps<
  TComponentAttributes extends string = string,
  TUIAttributes extends string = string,
  TSchema extends Schema = Schema,
  TInternalAttributes extends string = RecordPath<TSchema>,
> =
  | DynamicListCardAddItemProps<TComponentAttributes, TUIAttributes, TSchema, TInternalAttributes>
  | DynamicListFileAddFileProps<TSchema>;

export interface DynamicListFileAddFileProps<TSchema extends Schema = Schema>
  extends BaseDynamicListAddItemProps<DynamicListItemType.File, TSchema> {
  readonly maxFileSize?: string;
  readonly addFileImage?: string;
  readonly addFileCTA?: string;
  readonly dragAndDrop?: boolean;
  readonly fileTypePreset: FileTypePreset;
  readonly multiple?: boolean;
  readonly requestMap: FileUploadRequestMap;
  readonly responseMap: FileUploadResponseMap;
}

export interface DynamicListProps<
  TComponentAttributes extends string = string,
  TUIAttributes extends string = string,
  TItemType extends DynamicListItemType = DynamicListItemType,
  TAddItemSchema extends Schema = Schema,
> extends UniclientBaseComponentProps {
  readonly attribute: TComponentAttributes;
  readonly itemType: TItemType;
  readonly itemTemplate: TItemType extends DynamicListItemType.File
    ? null
    : Omit<DynamicListItemProps<TAddItemSchema, TComponentAttributes> & { readonly type: NoInfer<TItemType> }, 'type'>;
  readonly addItem?: Omit<
    DynamicListAddItemProps<NoInfer<TComponentAttributes>, TUIAttributes, TAddItemSchema> & {
      readonly type: NoInfer<TItemType>;
    },
    'type'
  >;
  readonly columns?: GridResponsiveColumns;
  // Only affects the desktop view, on mobile the add item is always closed at start
  readonly closedAtStart?: boolean;
}

export type WizardDynamicListProps<
  TComponentAttributes extends string = string,
  TUIAttributes extends string = string,
  TItemType extends DynamicListItemType = DynamicListItemType,
  TAddItemSchema extends Schema = Schema,
> = DynamicListProps<TComponentAttributes, TUIAttributes, TItemType, TAddItemSchema> &
  UniclientWizardComponentBase<'dynamic_list'>;

export function dynamicList<
  TComponentAttributes extends string = string,
  TUIAttributes extends string = string,
  TItemType extends Exclude<DynamicListItemType, DynamicListItemType.File> = Exclude<
    DynamicListItemType,
    DynamicListItemType.File
  >,
  TAddItemSchema extends Schema = Schema,
>(
  props: DynamicListProps<TComponentAttributes, TUIAttributes, TItemType, TAddItemSchema>,
): WizardDynamicListProps<TComponentAttributes, TUIAttributes, TItemType, TAddItemSchema> {
  return {
    ...props,
    type: 'dynamic_list',
  };
}

export function filesUpload<
  TComponentAttributes extends string = string,
  TUIAttributes extends string = string,
  TAddItemSchema extends Schema = Schema,
>(
  props: Omit<
    DynamicListProps<TComponentAttributes, TUIAttributes, DynamicListItemType.File, TAddItemSchema>,
    'itemTemplate' | 'itemType'
  >,
): WizardDynamicListProps<TComponentAttributes, TUIAttributes, DynamicListItemType.File, TAddItemSchema> {
  return {
    ...props,
    itemType: DynamicListItemType.File,
    itemTemplate: null,
    type: 'dynamic_list',
    columns: 3,
  };
}

export function saveDynamicListValues<TComponentAttributes extends string = string>(
  props: Omit<UniclientSaveDynamicListValuesAction<TComponentAttributes>, 'type'>,
): UniclientSaveDynamicListValuesAction<TComponentAttributes> {
  return { ...props, type: 'saveDynamicListValues' };
}
